import { SxProps } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import clsx from "clsx";
import {
  ASSESSMENT_VARIANTS,
  PLACEMENT_ORDER,
  PLACEMENT_ORDER_TYPE,
  PSYCH_CARE_REHAB_SPECIALIZATIONS,
  SEARCH_TYPES,
  SEARCH_TYPES_ARRAY,
  SEARCH_TYPE_PSYCHIATRIC_CARE,
  SOLUTION_MOBILE_CARE,
  SOLUTION_SHORT_TERM_STATIC_CARE,
  typeOfCareToRights,
} from "core/consts";
import {
  isAuctionRunning,
  isCareSearch,
  isHospitalSearch,
  isPsychCareSearch,
  isRehabSearch,
} from "core/model/auctions";
import {
  checkAllowedDuplicateSearchType,
  getSolutionsForPatientType,
  getSpecializations,
  hasPsychCareRehabSolution,
  showMultipleSpecializations,
  showSpecializations,
} from "core/model/patients";
import { activatePsychCare } from "core/model/utils/featureFlags";
import { OntologyType } from "core/model/utils/ontologies";
import {
  useGetOntologies,
  useGetOntology,
} from "core/model/utils/ontologies/hooks";
import { getValues } from "core/model/utils/ontologies/utils";
import { getQueryVariable } from "core/model/utils/urls";
import {
  Auction,
  Careseeker,
  GetOntologyType,
  Patient,
  SearchType,
} from "core/types";
import { getUnixTime } from "date-fns";
import { Banner } from "ds/ui";
import ActivableInput from "ds_legacy/components/ActivableInput";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import DatePickerInputField from "ds_legacy/components/DatePickerInputField";
import InfoCaption from "ds_legacy/components/InfoCaption";
import ConnectedRadioChipGroup from "ds_legacy/components/RadioChipGroup";
import RadioGroup from "ds_legacy/components/RadioGroup";
import RadioGroupV2 from "ds_legacy/components/RadioGroupV2";
import { SelectConnectedWithForm } from "ds_legacy/components/Select";
import ConnectedOntologySelectInput from "ds_legacy/components/SelectInput/Ontology";
import TextInputField from "ds_legacy/components/TextInputField";
import { CHECKBOX, dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  FONT_SIZE_14,
  FONT_WEIGHT_SEMI_BOLD,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import OntologyCheckboxGroup from "dsl/atoms/OntologyCheckboxGroup";
import { useUpdatedValue } from "dsl/hooks/useUpdatedValue";
import { AsteriskExplained } from "dsl/molecules/AsteriskExplained";
import ExternalIdKisField from "dsl/molecules/ExternalIdKisField";
import { FormWatcher, Show, getPlaceholderField } from "react-forms-state";
import { useFormRendererContext } from "react-forms-state/src/Context";
import { useLocation } from "react-router-dom";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { defaultSolutionsForSearchType } from "./modelDefinition";
import { PsychiatricCareSolutionsSelect } from "./PsychiatricCareSolutionsSelect";

export const textLabelWidth = dp(350);

export const solutionSortOrder = Object.fromEntries([
  ["1", 2],
  ["2", 3],
  ["3", 6],
  ["4", 4],
  ["5", 5],
  ["8", 1],
]);

export const patientTypeSortOrder = Object.fromEntries([
  ["1", 1],
  ["2", 2],
]);

function searchTypeDisabled({
  careseeker,
  patient,
  roles,
  searchType,
}: {
  careseeker: Careseeker;
  patient?: Patient;
  roles?: number[];
  searchType: SearchType;
}): { disable: boolean; hide: boolean } {
  if (!roles && isCareSearch(searchType))
    return { disable: false, hide: false };

  const duplicateAllowed = checkAllowedDuplicateSearchType({
    auctions: patient?.auctions,
    searchType,
  });

  if (!duplicateAllowed) return { disable: true, hide: false };

  const disabledByRoles = !typeOfCareToRights[searchType]?.some(
    (right) => roles?.includes(right),
  );

  const disabledByCareseeker = !careseeker.patient_types?.includes(searchType);

  const disabledSearchType = disabledByRoles || disabledByCareseeker;

  if (!disabledByRoles && disabledByCareseeker) {
    console.warn(
      `user with role for search type: ${searchType} for careseeker ${careseeker.id} without available patient type in: ${careseeker.patient_types}`,
    );
  }
  if (
    (
      [SEARCH_TYPES.HOME_CARE, SEARCH_TYPES.MEDICAL_SUPPLIES] as number[]
    ).includes(searchType)
  ) {
    return { disable: disabledByRoles, hide: disabledByCareseeker };
  }
  return { disable: disabledSearchType, hide: false };
}

const showFlexibleStartDate = (searchType: SearchType): boolean => {
  return (
    [
      SEARCH_TYPES.CARE,
      SEARCH_TYPES.HOSPITAL,
      SEARCH_TYPES.PSYCHIATRIC_CARE,
    ] as number[]
  ).includes(searchType);
};

const formatStation = ({ description = "", id = -1, name = "" }) => ({
  id,
  label: `${name}${description ? ` - ${description}` : ""}`,
  value: id,
});

export function getPatientTypeOptions({
  careseeker,
  getOntology,
  patient,
  roles,
  translations,
}: {
  careseeker: Careseeker | null;
  getOntology: GetOntologyType;
  patient?: Patient;
  roles?: number[];
  translations: Translations;
}) {
  if (!careseeker) return [];

  // TODO: Remove this filtering logic when activatePsychCare feature flag is removed.
  // Once the flag is removed, SEARCH_TYPES_ARRAY can be used directly.
  const searchTypes = activatePsychCare
    ? SEARCH_TYPES_ARRAY
    : SEARCH_TYPES_ARRAY.filter(
        (type) => type !== SEARCH_TYPE_PSYCHIATRIC_CARE,
      );

  return searchTypes
    .map((searchType) => {
      const { disable, hide } = searchTypeDisabled({
        careseeker,
        patient,
        roles,
        searchType,
      });
      if (hide) return null;
      return {
        disabled: disable,
        name: getOntology({ key: searchType, type: "patientType" }),
        tooltipText: disable ? translations.patient.upgradePlan : undefined,
        value: searchType,
      };
    })
    .truthy();
}

export const getSolutionOptions = (patientType: SearchType) =>
  getValues("solution").filter((sol) =>
    getSolutionsForPatientType(patientType).includes(sol),
  );

export function useFilteredOptions({
  exclude,
  include,
  type,
}: {
  exclude?: number[] | null;
  include?: number[] | null;
  type: OntologyType;
}) {
  const getOntologies = useGetOntologies();
  let values = getOntologies({ sort: true, type });

  if (include) {
    values = values.filter((v) => include?.includes(v.value));
  }

  if (exclude) {
    values = values.filter((v) => exclude && !exclude.includes(v.value));
  }
  return values.map(({ label, value }) => ({
    id: value,
    label,
    value,
  }));
}

function SpecializationSelect({
  careseeker,
  isRequired,
  searchType,
  solutions,
}: {
  careseeker: Careseeker;
  isRequired?: boolean;
  searchType: SearchType;
  solutions?: number[] | null;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const isPsychCareWithRehab =
    isPsychCareSearch(searchType) && hasPsychCareRehabSolution(solutions);

  const showSpecialization = showSpecializations({
    searchType,
    solutions,
  });
  const specializations = getSpecializations({
    careseeker,
    searchType,
  });
  const showMultiple = showMultipleSpecializations({ searchType });

  const options = useFilteredOptions({
    include: specializations,
    type: "specializations",
  });

  if (isPsychCareWithRehab) {
    const radioOptions = PSYCH_CARE_REHAB_SPECIALIZATIONS.map(
      (specializationId) => ({
        id: specializationId,
        label: getOntology({
          key: specializationId,
          type: "specializations",
        }),
        value: specializationId,
      }),
    );

    return (
      <RadioGroup
        elementName="specializations"
        isRequired={isRequired}
        label={translations.ontologies.specializations.key}
        labelStyle={{
          fontSize: FONT_SIZE_14,
          fontWeight: FONT_WEIGHT_SEMI_BOLD,
        }}
        options={radioOptions}
      />
    );
  }

  // Default behavior: show all specializations as dropdown
  if (!showSpecialization) return null;

  return (
    <SelectConnectedWithForm
      elementName="specializations"
      label={
        showMultiple ? translations.ontologies.specializations.key : undefined
      }
      multiple={showMultiple}
      options={options}
      placeholder={translations.general.selectDefaultPlaceholderShort}
      required={isRequired}
      width="100%"
    />
  );
}

function ShortAssessmentSelect() {
  const translations = useTranslations();

  return (
    <RadioGroup
      elementName="assessment_variant"
      label={translations.patient.complexityPatient}
      labelStyle={{ fontSize: FONT_SIZE_14 }}
      marginBox={margin(1, 0, 0)}
      marginWrapper={margin(1, 0, 0, -0.5)}
      options={[
        {
          id: 1,
          label: translations.careseeker.shortMobileAssessment,
          // @ts-ignore
          value: "mobile-short",
        },
        {
          id: 2,
          label: translations.careseeker.longMobileAsssessment,
          // @ts-ignore
          value: "",
        },
      ]}
    />
  );
}

type RequiredServiceFieldProps = {
  fieldName: string;
  label: string;
};

function RequiredServiceFieldItem({
  fieldName,
  label,
}: RequiredServiceFieldProps) {
  const translations = useTranslations();
  return (
    <div style={{ margin: margin(0, 0, 1, 0) }}>
      <ActivableInput
        elementName={getPlaceholderField(fieldName)}
        label={label}
      >
        <TextInputField
          elementName={`${fieldName}_description`}
          inputType={CHECKBOX}
          multiline
          placeholder={translations.patient.requiredServices.labelremarks}
          style={{ marginLeft: dp(-14), maxWidth: dp(520), width: "100%" }}
        />
      </ActivableInput>
    </div>
  );
}

function RequiredServicesField() {
  const translations = useTranslations();

  return (
    <div style={{ margin: margin(1, 0, 0) }}>
      <Subheading
        margin={margin(0, 0, 1, 0)}
        style={{ fontSize: FONT_SIZE_14, fontWeight: FONT_WEIGHT_SEMI_BOLD }}
      >
        {translations.patient.requiredServices.label}
      </Subheading>
      <RequiredServiceFieldItem
        fieldName="psychiatric_home_nursing"
        label={translations.patient.requiredServices.psychiatricHomeNursing}
      />
      <RequiredServiceFieldItem
        fieldName="integration_assistance"
        label={translations.patient.requiredServices.integrationAssistance}
      />
      <RequiredServiceFieldItem
        fieldName="sociotherapy"
        label={translations.patient.requiredServices.sociotherapy}
      />
      <RequiredServiceFieldItem
        fieldName="substitute_offers"
        label={translations.patient.requiredServices.substituteOffers}
      />
      <RequiredServiceFieldItem
        fieldName="daily_activities"
        label={translations.patient.requiredServices.dailyActivities}
      />
      <RequiredServiceFieldItem
        fieldName="psych_protected_area"
        label={translations.patient.requiredServices.protectedArea}
      />
      <RequiredServiceFieldItem
        fieldName="psych_closed_area"
        label={translations.patient.requiredServices.closedArea}
      />
    </div>
  );
}

function SolutionsSelect({ searchType }: { searchType: SearchType }) {
  const translations = useTranslations();

  if (isPsychCareSearch(searchType)) {
    return <PsychiatricCareSolutionsSelect />;
  }

  return (
    <OntologyCheckboxGroup
      elementName="solutions"
      include={getSolutionOptions(searchType)}
      isRequired
      label={translations.patient.solutionLabel}
      labelStyle={{
        fontSize: FONT_SIZE_14,
        margin: margin(0),
      }}
      order={solutionSortOrder}
      sideMutation={(value, mutateElement) => {
        mutateElement(null, "assessment_variant");
      }}
      type="solution"
      wrapperStyle={{ margin: margin(0) }}
    />
  );
}

function PlacementOrderFieldReset({
  status,
}: {
  status: number | null | undefined;
}) {
  const { onChange } = useFormRendererContext();

  useUpdatedValue((_newStatus, oldStatus) => {
    if (oldStatus == null) return;
    onChange(null, "patient.profile.placement_order.type", null);
    onChange(null, "patient.profile.placement_order.exhibiting_state", null);
  }, status);

  return null;
}

function PlacementOrderField() {
  const translations = useTranslations();

  const subFormContent = (
    <div style={{ marginLeft: dp(20), marginTop: margin(1, 0) }}>
      <RadioGroupV2
        elementName="patient.profile.placement_order.type"
        formLabelSx={visuallyHidden as SxProps}
        label={translations.patient.placementOrder.label}
        options={[
          {
            label: translations.patient.placementOrder.civilLawPlacement,
            value: PLACEMENT_ORDER_TYPE.CIVIL_LAW_PLACEMENT,
          },
          {
            label: translations.patient.placementOrder.publicAccommodation,
            value: PLACEMENT_ORDER_TYPE.PUBLIC_ACCOMMODATION,
          },
        ]}
      />
      <TextInputField
        elementName="patient.profile.placement_order.exhibiting_state"
        label={translations.patient.requiredServices.exhibitingFederalState}
        style={{ margin: margin(0, 0, 0, 0), width: textLabelWidth }}
      />
    </div>
  );

  return (
    <>
      <FormWatcher watchPath="patient.profile.placement_order.status">
        {({ watchedValue: status }) => {
          return <PlacementOrderFieldReset status={status} />;
        }}
      </FormWatcher>
      <RadioGroup
        elementName="patient.profile.placement_order.status"
        label={translations.patient.placementOrder.label}
        labelStyle={{
          fontSize: FONT_SIZE_14,
          fontWeight: FONT_WEIGHT_SEMI_BOLD,
        }}
        marginBox={margin(1, 0, 0)}
        marginWrapper={margin(1, 0, 0, -0.5)}
        options={[
          {
            id: 1,
            label: translations.patient.placementOrder.atHand,
            subForm: subFormContent,
            value: PLACEMENT_ORDER.AT_HAND,
          },
          {
            id: 2,
            label: translations.patient.requiredServices.appliedFor,
            subForm: subFormContent,
            value: PLACEMENT_ORDER.APPLIED_FOR,
          },
          {
            id: 3,
            label: translations.patient.placementOrder.notAtHand,
            value: PLACEMENT_ORDER.NOT_AT_HAND,
          },
        ]}
        showOnlyActive
      />
    </>
  );
}

function SearchTypeField({
  careseeker,
  isCreating,
  roles,
}: {
  careseeker: Careseeker;
  isCreating: boolean;
  roles: Array<number>;
}) {
  const { resetValidation } = useFormRendererContext();
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const patientOptions = getPatientTypeOptions({
    careseeker,
    getOntology,
    roles,
    translations,
  });

  const sideMutation = (
    newValue: SearchType,
    mutateElement: (s: any, k: string) => void,
  ) => {
    resetValidation([
      "specializations",
      "solutions",
      "start_date",
      "care_duration_in_days",
    ]);

    mutateElement(defaultSolutionsForSearchType(newValue), "solutions");
    mutateElement(null, "care_duration_in_days");

    const today = getUnixTime(new Date());
    const patientTypeSpecializations = getSpecializations({
      careseeker,
      searchType: newValue,
    });

    if (isHospitalSearch(newValue)) {
      mutateElement(today, "start_date");
    }

    if (patientTypeSpecializations.length == 1) {
      mutateElement(
        {
          id: patientTypeSpecializations[0],
          label: getOntology({
            key: patientTypeSpecializations[0],
            type: "specializations",
          }),
          value: patientTypeSpecializations[0],
        },
        "specializations",
      );
    } else {
      mutateElement(null, "specializations");
    }
  };

  return (
    <ConnectedRadioChipGroup
      elementName="search_type"
      formLabelSx={{
        fontSize: FONT_SIZE_14,
        padding: padding(0),
      }}
      id="search_type"
      isDisabled={!isCreating}
      isRequired
      label={translations.patient.patientType}
      options={patientOptions}
      radioGroupSx={{
        margin: margin(1, 0, 0),
        width: textLabelWidth,
      }}
      sideMutation={sideMutation}
      sortOrder={patientTypeSortOrder}
    />
  );
}

export function PatientCreateForm({
  auction,
  careseeker,
  isCreating,
  roles,
}: {
  auction?: Auction;
  careseeker: Careseeker;
  isCreating: boolean;
  roles: Array<number>;
}) {
  const translations = useTranslations();
  const location = useLocation();
  const externalIdQueryVar =
    getQueryVariable(location.search, "external_id") ||
    auction?.patient?.external_id;

  const searchStarted = auction && isAuctionRunning(auction.status);
  const stationOptions = careseeker.stations_full?.map(formatStation) ?? [];

  return (
    <div className="flex max-w-xl flex-col px-4 pb-8">
      {isCreating && (
        <>
          <AsteriskExplained customMargin={margin(1, 0, 0, 0)} />
          <Title style={{ margin: padding(1, 0) }}>
            {translations.patient.createPatient}
          </Title>
        </>
      )}

      <Subheading bold className="m-0! mt-4!">
        {translations.patient.hospitalStay}
      </Subheading>

      <div className="mt-4 flex flex-col gap-2">
        <ExternalIdKisField
          careseeker={careseeker}
          externalIdQueryVar={externalIdQueryVar}
          textLabelWidth={textLabelWidth}
        />

        {careseeker.stations_full && (
          <div data-testid="station">
            <SelectConnectedWithForm
              elementName="station_full_id"
              fontSize={FONT_SIZE_14}
              label={translations.patient.station}
              options={stationOptions}
              placeholder={translations.general.selectDefaultPlaceholderShort}
              sideMutation={(
                value: { id: number } | undefined,
                mutateElement: (v: any, name: string) => void,
              ) => {
                if (value) {
                  const station = careseeker.stations_full?.find(
                    ({ id }) => id == value.id,
                  );
                  if (station?.phone_number) {
                    mutateElement(
                      station.phone_number,
                      "doctor_in_charge_in_hospital_phone",
                    );
                  }
                }
              }}
              value={stationOptions.length === 1 && stationOptions}
              width={textLabelWidth}
              withPortal
            />
          </div>
        )}
        <div>
          <TextInputField
            elementName="doctor_in_charge_in_hospital_phone"
            hasCustomValidation
            label={translations.patient.stationPhoneNumber}
            style={{ margin: margin(1, 0), width: textLabelWidth }}
          />
        </div>
      </div>
      <div className="mt-4 flex flex-col">
        <Subheading bold className="m-0! mb-4!">
          {translations.patient.transfer}
        </Subheading>
        {searchStarted && (
          <InfoCaption text={translations.patient.transferChanges} />
        )}
      </div>
      <div>
        <SearchTypeField
          careseeker={careseeker}
          isCreating={isCreating}
          roles={roles}
        />
      </div>
      <FormWatcher watchPath="search_type">
        {({ watchedValue: searchType }) => {
          const isSearchTypeHidden = [
            SEARCH_TYPES.MEDICAL_SUPPLIES,
            SEARCH_TYPES.HOME_CARE,
          ].includes(searchType);
          return (
            <>
              {searchType && (
                <div
                  className={clsx({
                    hidden: isSearchTypeHidden,
                    "mt-4 mb-4": !isSearchTypeHidden,
                  })}
                >
                  <SolutionsSelect searchType={searchType} />
                </div>
              )}

              <FormWatcher watchPath="solutions">
                {({ watchedValue: solutions }) => {
                  const shouldShowSpecialization = showSpecializations({
                    searchType,
                    solutions,
                  });
                  const showShortMobileAssessmentSelect =
                    solutions?.length == 1 &&
                    solutions[0] === SOLUTION_MOBILE_CARE;

                  return (
                    <>
                      <div
                        hidden={!shouldShowSpecialization}
                        style={{ width: textLabelWidth }}
                      >
                        <SpecializationSelect
                          careseeker={careseeker}
                          isRequired
                          searchType={searchType}
                          solutions={solutions}
                        />
                      </div>
                      {isPsychCareSearch(searchType) && (
                        <>
                          <RequiredServicesField />
                          <PlacementOrderField />
                        </>
                      )}
                      {isRehabSearch(searchType) && (
                        <div className="mt-4 flex flex-col gap-4">
                          <DatePickerInputField
                            key="operation_date"
                            elementName="operation_date"
                            hasCustomValidation
                            inputSx={{
                              margin: margin(0),
                              width: textLabelWidth,
                            }}
                            isRequired
                            label={
                              translations.patient.medicalDiagnosis
                                .operationDate
                            }
                          />
                          <TextInputField
                            elementName="operation_description"
                            label={
                              translations.patient.medicalDiagnosis
                                .operationDescription
                            }
                            multiline
                            placeholder={
                              translations.patient.medicalDiagnosis
                                .operationDescriptionPlaceholder
                            }
                            style={{
                              margin: margin(0),
                              width: textLabelWidth,
                            }}
                          />
                        </div>
                      )}
                      {isPsychCareSearch(searchType) && (
                        <Subheading
                          bold
                          className="m-0! mt-4! mb-4!"
                          style={{ fontSize: FONT_SIZE_14 }}
                        >
                          {translations.patient.periodAftercare}
                        </Subheading>
                      )}
                      <div
                        className={clsx(
                          "flex flex-col gap-4",
                          !isPsychCareSearch(searchType) && "mt-4",
                        )}
                      >
                        <DatePickerInputField
                          key="start_date"
                          elementName="start_date"
                          inputSx={{
                            margin: margin(0),
                            width: textLabelWidth,
                          }}
                          isRequired
                          label={translations.patient.expectedStartDate}
                          shouldDisablePast
                        />
                        <div hidden={!isRehabSearch(searchType)}>
                          <DatePickerInputField
                            key="discharge_date"
                            elementName="discharge_date"
                            hasCustomValidation
                            inputSx={{
                              margin: margin(0),
                              width: textLabelWidth,
                            }}
                            label={translations.patient.expectedDischargeDate}
                          />
                        </div>
                      </div>
                      {showFlexibleStartDate(searchType) && (
                        <Show elementName="start_date_flexible">
                          <div>
                            <CheckboxInputField
                              elementName="start_date_flexible"
                              label={translations.patient.flexibleDate}
                              margin={margin(0.25, 0, 1, -1.5)}
                            />
                          </div>
                        </Show>
                      )}
                      {(isCareSearch(searchType) ||
                        isPsychCareSearch(searchType)) && (
                        <>
                          <div style={{ width: textLabelWidth }}>
                            <ConnectedOntologySelectInput
                              elementName="care_duration_in_days"
                              isFullWidth
                              label={translations.patient.careDuration}
                              placeholder={translations.patient.careDuration}
                              required
                              size="small"
                              type="careDurations"
                            />
                          </div>
                          <div
                            hidden={
                              !isPsychCareSearch(searchType) &&
                              !solutions?.includes(
                                SOLUTION_SHORT_TERM_STATIC_CARE,
                              )
                            }
                          >
                            <CheckboxInputField
                              elementName="interested_long_term_stay"
                              label={
                                translations.patient.interestedStayLongTerm
                              }
                              margin={margin(0, 0, 0, -1.5)}
                            />
                          </div>
                        </>
                      )}
                      <div
                        className={clsx({
                          flex: showShortMobileAssessmentSelect,
                          hidden: !showShortMobileAssessmentSelect,
                        })}
                      >
                        <ShortAssessmentSelect />
                      </div>
                      <div
                        className={clsx({
                          hidden: !isRehabSearch(searchType),
                          "mt-4 flex flex-col gap-2": isRehabSearch(searchType),
                        })}
                      >
                        <CheckboxInputField
                          elementName="direct_transfer_necessary"
                          label={translations.patient.directTransferNecessary}
                        />
                        <CheckboxInputField
                          customValue={ASSESSMENT_VARIANTS.ELECTIVE_REHAB}
                          elementName="assessment_variant"
                          label={
                            translations.patient.electivePatientNotInHospitalYet
                          }
                        />
                        <Banner
                          color="primary"
                          message={
                            translations.careproviderApp.settings.payers
                              .infofieldRehabShortInsurances
                          }
                          withIcon={false}
                        />
                      </div>
                    </>
                  );
                }}
              </FormWatcher>
            </>
          );
        }}
      </FormWatcher>
    </div>
  );
}
